import {prop} from 'ramda';
import {
  BehaviorSubject,
  bufferCount,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  pairwise,
  scan,
  skip,
  skipWhile,
  startWith,
  takeUntil,
  tap,
} from 'rxjs';

import {Action} from './action';
import Decimal from './break_infinity';
import {CreatureTemplates} from './creature_templates';
import * as D from './decimal_utils';
import {Positionable} from './landmark';
import {Resource} from './resource';
import {ri, RunnerIdle} from './runner-idle';
import {Stat} from './stat';
import {MakeOptional, ResourceName, StatName} from './types/runner-idle';
import {UnlockableLitElement} from './unlockable-lit-element';
import {assignIfDefined} from './utils';
import {Vector} from './Vector';

export interface Drawable {
  // #region Properties (2)

  fillColor: [number, number, number, number?];
  strokeColor: [number, number, number, number?];

  // #endregion Properties (2)

  // #region Public Methods (1)

  draw(p: import('p5'), zoomScale: Decimal): void;

  // #endregion Public Methods (1)
}
export type Entity = RunnerIdle | Actor;
/**
 * An in-game thing located on the map that acts every tick.
 * Consider making more component pattern-y.
 * */
export class Actor implements Positionable, Drawable, Action {
  // #region Properties (20)

  public actions: Action[] = [];
  public bodySize: Decimal = new Decimal(1);
  public dead$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public deathActions: Action[] = [];
  public displayName = 'Actor';
  public drawPosition: Vector = new Vector();
  public drawPosition$!: Observable<Vector>;
  public fillColor: [number, number, number] = [0, 0, 0];
  public interactionRange: Decimal = new Decimal(1);
  public makeActions: (this: Actor) => Action[] = () => [];
  public makeDeathActions: (this: Actor) => Action[] = () => [];
  public name = 'actor';
  public position: Vector = new Vector();
  public position$: BehaviorSubject<Vector> = new BehaviorSubject(
    new Vector(0, 0, 0)
  );
  public radius: Decimal = new Decimal(1);
  public resources: Partial<
    Record<ResourceName, MakeOptional<Resource, keyof UnlockableLitElement>>
  > = {};
  public spawnedBy = '';
  public stats: Partial<
    Record<StatName, MakeOptional<Stat, keyof UnlockableLitElement>>
  > = {};
  public strokeColor: [number, number, number, number] = [0, 0, 0, 0];
  public tags: Set<string> = new Set();

  // #endregion Properties (20)

  // #region Constructors (1)

  constructor() {
    this.setupPositionSubscription();
  }

  // #endregion Constructors (1)

  // #region Public Static Methods (2)

  /**
   * Make an instance of this class.
   */
  public static create(overrides: Partial<Actor>) {
    const instance = new Actor();
    assignIfDefined(instance, overrides);
    instance.actions = instance.makeActions();
    instance.deathActions = instance.makeDeathActions();
    instance.actions
      .filter(prop('properties'))
      .forEach((c) => assignIfDefined(instance, c.properties!));
    instance.deathActions
      .filter(prop('properties'))
      .forEach((c) => assignIfDefined(instance, c.properties!));
    instance.position$.next(overrides.position ?? instance.position);
    return instance;
  }

  public static fromJSON(jsonObject): Actor {
    const instance = Actor.create({
      ...jsonObject,
      position: new Vector(...jsonObject.position),
      interactionRange: new Decimal(jsonObject.interactionRange),
      bodySize: new Decimal(jsonObject.bodySize),
      resources: Object.fromEntries(
        Object.entries(jsonObject.resources).map(([key, value]) => [
          key,
          {
            name: key,
            value: new Decimal(value.value),
            capacity: new Decimal(value.capacity),
          },
        ])
      ),
      stats: Object.fromEntries(
        Object.entries(jsonObject.stats).map(([key, value]) => [
          key,
          {
            name: key,
            value: new Decimal(value.value),
          },
        ])
      ),
    });
    return instance;
  }

  // #endregion Public Static Methods (2)

  // #region Public Methods (8)

  public act() {
    const _this = this; // This is so fucking cursed.
    this.actions.forEach((c) => c.act.bind(_this)());
  }

  public draw(p: import('p5'), zoomScale: Decimal): void {
    const x = this.drawPosition.x.div(zoomScale).toNumber();
    const y = this.drawPosition.y.div(zoomScale).toNumber();
    p.push();
    p.fill(...this.fillColor, 127);
    p.stroke(...this.strokeColor);
    if (ri.settings.map.showActorNames) {
      p.push();
      p.translate(x, y);
      p.scale(1, -1);
      p.scale(ri.settings.map.actorNameScale.value);
      p.stroke([0, 0, 0]);
      p.text(this.displayName, 0, 0);
      p.pop();
    }
    p.ellipse(
      x,
      y,
      zoomScale
        .reciprocal()
        .times(this.interactionRange.times(2))
        .max(1)
        .toNumber(),
      zoomScale
        .reciprocal()
        .times(this.interactionRange.times(2))
        .max(1)
        .toNumber()
    );
    p.pop();
  }

  /** Stub for gaining resources/stats */
  public gain() {}

  /**
   * What happens when the player gets in range of this?
   */
  public interact(distance: Decimal, entity: Entity = ri) {
    // console.log('interact', this.name, entity.name);
    this.onInteract(distance, entity);
  }
  onInteract(distance: Decimal, entity: Entity = ri) {}

  /** Returns true if successfully moved, false if blocked. */
  public moveTo(nextPosition: Vector): boolean {
    const oldPosition = this.position;
    // Check if this location is movable
    if (
      this.tags.has('flying') ||
      ri.pointHasTag(nextPosition, 'passable') ||
      !(
        ri.pointHasTag(nextPosition, 'water') && !this.tags.has('waterwalking')
      ) ||
      !(!ri.pointHasTag(nextPosition, 'wall') && !this.tags.has('wallwalking'))
    ) {
      this.position$.next(nextPosition);
      return true;
    }
    return false;
  }

  public setupPositionSubscription() {
    this.drawPosition$ = combineLatest([
      ri.animationFrame$,
      this.position$,
    ]).pipe(
      map(([_, position]) => position),
      pairwise(),
      scan((acc, [lastPosition, currentPosition]) => {
        if (lastPosition === currentPosition) {
          if (acc.z.lt(0)) return currentPosition;
          return acc.plus(currentPosition.minus(acc).times(0.1));
        }
        return lastPosition;
      }, new Vector(0, 0, -1))
    );
    this.position$.subscribe((pos) => (this.position = pos));
    combineLatest([this.position$, ri.position$])
      .pipe(
        map(([thisPos, playerPos]) => thisPos.distance(playerPos)),
        filter((dist) => dist.lte(this.interactionRange)),
        skip(1), // Skip before positions are initialized
        takeUntil(this.dead$.pipe(filter(Boolean)))
      )
      .subscribe((distance) => this.interact(distance, ri));
    // TODO this is O(n^2) (slightly worse even?), implement scanline or something
    this.drawPosition$.subscribe((value) => (this.drawPosition = value));
  }

  public takeDamage(_damage: Decimal, _attacker: Entity) {}

  public toJSON() {
    return {
      name: this.name,
      displayName: this.displayName,
      spawnedBy: this.spawnedBy,
      bodySize: this.bodySize,
      position: this.position,
      radius: this.radius,
      resources: this.resources,
      stats: this.stats,
      interactionRange: this.interactionRange,
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
    };
  }

  // #endregion Public Methods (8)
}
